import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { BADGE, FONT_SIZE, INK, NEUTRAL, RADIUS, SURFACE } from "../../../theme/tokens";
import { type OutreachPaceReason, OutreachPaceReasonKind, OutreachUrgency, type RequestOutreach } from "../../../utils/models";
import MetaChip, { type MetaChipTone } from "../../UIToolkit/Badges/MetaChip";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import { levelIndexOf, levelNumberOf } from "./outreachGroupTypes";
import { dayMetaOf, type OutreachCopy } from "./useOutreachCopy";

type Props = { outreach: RequestOutreach; copy: OutreachCopy; now: DateTime };

const URGENCY_TONE: Record<OutreachUrgency, MetaChipTone> = {
	[OutreachUrgency.Normal]: "primary",
	[OutreachUrgency.Urgent]: "warning",
	[OutreachUrgency.Critical]: "error",
};

/** The weekday and shift type a fill-history reason names: "Thu" and "evening". */
const WEEKDAY_FORMAT = "ccc";

const reasonTextOf = (t: TFunction, reason: OutreachPaceReason, shift: RequestOutreach["shift"]): { label: string; value: string } => {
	switch (reason.kind) {
		case OutreachPaceReasonKind.LeadTime:
			return {
				label: t("request.outreach.pace.lead_time.label"),
				value:
					reason.daysBefore === 0 ? t("request.outreach.pace.lead_time.same_day") : t("request.outreach.pace.lead_time.value", { count: reason.daysBefore }),
			};
		case OutreachPaceReasonKind.FillHistory:
			return reason.hardToFill
				? { label: t("request.outreach.pace.fill.hard_label"), value: t("request.outreach.pace.fill.hard_value") }
				: {
						label: t("request.outreach.pace.fill.easy_label"),
						value:
							shift.shiftType === null
								? t("request.outreach.pace.fill.easy_value_any", { weekday: shift.start.toFormat(WEEKDAY_FORMAT) })
								: t("request.outreach.pace.fill.easy_value", {
										weekday: shift.start.toFormat(WEEKDAY_FORMAT),
										shiftType: t(`request.outreach.shift_type.${shift.shiftType}`),
									}),
					};
		case OutreachPaceReasonKind.Staffing: {
			const { scheduled, target, minimum } = reason;
			const args = { scheduled, target, count: target - scheduled };
			const key = scheduled >= target ? "full" : scheduled >= minimum ? "short" : "below_minimum";
			return { label: t("request.outreach.pace.staffing.label"), value: t(`request.outreach.pace.staffing.${key}`, args) };
		}
	}
};

/** The levels still to come, each with when it starts on its own; none while holding. */
const Schedule: FC<Props> = ({ outreach, copy, now }) => {
	const { t, i18n } = useTranslation();
	const { effort } = outreach;
	const current = levelIndexOf(effort.level);
	const later = effort.schedule.filter((entry, index) => index > current && entry.startsAt !== null);
	const heading = effort.holding
		? t("request.outreach.pace.schedule.holding", { level: copy.levelNumber(effort.level) })
		: later.length > 0
			? t("request.outreach.pace.schedule.title")
			: t("request.outreach.pace.schedule.last");
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<Typography component="h4" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.SECONDARY }}>
				{heading}
			</Typography>
			{!effort.holding && later.length > 0 && (
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
					{later.map(({ level, startsAt }) => {
						if (startsAt === null) throw new Error(`Outreach: level ${level} is listed as coming but has no start`);
						return (
							<Box
								key={level}
								data-testid={`outreach-pace-next-${level}`}
								sx={{ px: 1.5, py: 1.25, borderRadius: 1, backgroundColor: NEUTRAL[50], display: "flex", flexDirection: "column", gap: 0.25 }}
							>
								<Box
									component="span"
									sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.PRIMARY }}
								>
									<Box
										component="span"
										aria-hidden
										sx={{
											width: BADGE.PILL_SIZE,
											height: BADGE.PILL_SIZE,
											borderRadius: BADGE.PILL_RADIUS_FULL,
											border: (theme) => `2px solid ${theme.palette.primary.light}`,
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											fontSize: FONT_SIZE.LABEL_MICRO,
											fontWeight: 700,
											color: "primary.main",
										}}
									>
										{levelNumberOf(level)}
									</Box>
									{copy.levelNumber(level)}
								</Box>
								<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.PRIMARY }}>
									{copy.levelName(level)}
								</Typography>
								<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY }}>
									{dayMetaOf(t, startsAt, now)}
								</Typography>
								<Typography component="span" sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 600, color: "primary.dark" }}>
									{startsAt.toRelative({ base: now, locale: i18n.language })}
								</Typography>
							</Box>
						);
					})}
				</Box>
			)}
		</Box>
	);
};

/** Why the plan steps up when it does: the shift's urgency and the reasons behind it, how levels work, and what comes next. */
export const OutreachPaceWhy: FC<Props> = ({ outreach, copy, now }) => {
	const { t } = useTranslation();
	const { urgency, paceReasons } = outreach.effort;
	return (
		<Box
			data-testid="outreach-pace-why"
			sx={{ mt: 0.75, p: 1.5, borderRadius: RADIUS.CONTROL / RADIUS.CARD, backgroundColor: SURFACE, display: "flex", flexDirection: "column", gap: 1.25 }}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.SECONDARY }}>
					{t("request.outreach.pace.urgency_label")}
				</Typography>
				<MetaChip tone={URGENCY_TONE[urgency]} label={t(`request.outreach.urgency.${urgency}`)} dataTestId="outreach-pace-urgency" />
			</Box>
			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
				{paceReasons.map((reason) => {
					const { label, value } = reasonTextOf(t, reason, outreach.shift);
					return (
						<Box
							key={reason.kind}
							data-testid={`outreach-pace-${reason.kind}`}
							sx={{ px: 1.5, py: 1.25, borderRadius: 1, backgroundColor: NEUTRAL[50], display: "flex", flexDirection: "column", gap: 0.375 }}
						>
							<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, fontWeight: 600, color: "primary.main" }}>
								{label}
							</Typography>
							<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.PRIMARY }}>
								{value}
							</Typography>
							<Box sx={{ alignSelf: "flex-start", mt: 0.25 }}>
								<MetaChip
									size="small"
									tone={reason.speedsUp ? "warning" : "success"}
									label={t(reason.speedsUp ? "request.outreach.pace.effect.speeds_up" : "request.outreach.pace.effect.calm")}
								/>
							</Box>
						</Box>
					);
				})}
			</Box>
			<InlineHint tone="info" icon={<AutoAwesomeRoundedIcon fontSize="small" />}>
				<Box component="strong" sx={{ fontWeight: 600 }}>
					{t("request.outreach.pace.note_title")}
				</Box>{" "}
				{t("request.outreach.pace.note")}
			</InlineHint>
			<Schedule outreach={outreach} copy={copy} now={now} />
		</Box>
	);
};
